import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { BaseFormField } from '@forthbridge/forms';

import { SelectionButton } from '@shared/components/selection-button/selection-button';
import { type SelectionButtonConfig } from '@shared/components/selection-button/selection-button.types';

/**
 * A single-select button group form field.
 * Displays a group of buttons where only one can be selected at a time.
 * Clicking a selected button deselects it (toggles to null).
 */
@Component({
  selector: 'app-single-select-button-group',
  templateUrl: './single-select-button-group.html',
  imports: [SelectionButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleSelectButtonGroup extends BaseFormField<string | null> {
  /** The options to display as buttons */
  readonly options = input<Array<{ label: string; value: string }>>([]);

  /** Whether to set the selectedValue signal with the actual value when writeValue is called */
  readonly shouldSetInitialValue = input(false);

  protected readonly columns = input(2);
  private readonly selectedValue = signal<string | null>(null);
  private isInitialValueHandled = false;

  protected readonly buttonConfigs = computed<SelectionButtonConfig[]>(() => {
    if (!this.control) return [];

    const selected = this.selectedValue();
    return this.options().map((option) => ({
      label: option.label,
      value: option.value,
      state: this.isDisabledState ? 'disabled' : selected === option.value ? 'selected' : 'idle',
    }));
  });

  override writeValue(value: string | null): void {
    // First writeValue call = initial value
    if (!this.isInitialValueHandled) {
      this.isInitialValueHandled = true;
      if (this.shouldSetInitialValue()) {
        this.applyValue(value);
      } else {
        // Ignore initial value, clear to null
        this.applyValue(null);
      }
      return;
    }
    // Subsequent writeValue calls - always apply
    this.applyValue(value);
  }

  private applyValue(value: string | null): void {
    super.writeValue(value);
    this.selectedValue.set(value);
    // Sync the actual FormControl value to match
    if (this.control && this.control.value !== value) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  protected select(value: string | undefined): void {
    if (!value || !this.control) return;
    // Toggle: if already selected, deselect; otherwise select
    const newValue = this.control.value === value ? null : value;
    this.control.setValue(newValue);
    this.selectedValue.set(newValue);
  }
}
